import { Heart, Target, Eye, Award, Users, Sparkles } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1711182673833-7e11dffa0eec?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const NETWORK = "https://images.unsplash.com/photo-1590650046871-92c887180603?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

const VALUES = [
  { icon: <Award />, title: "Empowerment", text: "Practical workshops that change careers — negotiation, leadership, fundraising, financial literacy and personal branding." },
  { icon: <Heart />, title: "Health & Wellness", text: "Wellness retreats, fertility & menopause panels, mental-health circles and fitness pods built around the lived realities of Indian women." },
  { icon: <Sparkles />, title: "CSR · Lift as we rise", text: "Every membership funds skilling, scholarships and safety initiatives for under-served girls and women across Telangana." },
  { icon: <Users />, title: "Earned Belonging", text: "Curated membership. Hand-vetted by chapter leads. Quiet rooms over loud ballrooms." },
];

const TEAM = [
  { name: "Founder", role: "Chapter Lead · Hyderabad", img: "https://images.unsplash.com/photo-1770627016447-cb9d29ed0398?w=600", bio: "Building WOWSHE's founding chapter from Hyderabad." },
  { name: "Programs Lead", role: "Workshops & Empowerment", img: "https://images.unsplash.com/photo-1711182673833-7e11dffa0eec?w=600", bio: "Curating the 36-workshop 2026 calendar." },
  { name: "Wellness Lead", role: "Health & Wellness", img: "https://images.unsplash.com/photo-1551731409-43eb3e517a1a?w=600", bio: "Designing wellness retreats, panels and pods." },
  { name: "CSR Lead", role: "Women & Girls Outreach", img: "https://images.unsplash.com/photo-1763475945531-7241e0015363?w=600", bio: "Building the skilling & scholarship programs." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Intro */}
      <section className="section-pad container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="overline">About WOWSHE</p>
            <h1 className="font-display text-5xl md:text-6xl text-aubergine mt-6 leading-[1.05] text-balance">
              Launching in Hyderabad. <em className="text-rose not-italic">Built for the women of India.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-plumSoft leading-relaxed font-light">
              WOWSHE — <strong className="text-aubergine font-normal">Women of Wisdom, Success, Happiness & Empowerment</strong> —
              is an invitation-grade community for ambitious women. We launch in Hyderabad in 2026
              with our founding cohort, then expand chapter by chapter across India.
            </p>
          </div>
        </div>
        <div className="mt-14 grid grid-cols-12 gap-6">
  <div className="col-span-12 md:col-span-8 h-[400px] md:h-[520px] overflow-hidden rounded-2xl">
    <img
      src={ABOUT_IMG}
      alt="About"
      className="h-full w-full object-cover object-top"
    />
  </div>

  <div className="col-span-12 md:col-span-4 h-[400px] md:h-[520px] overflow-hidden rounded-2xl">
    <img
      src={NETWORK}
      alt="Networking"
      className="h-full w-full object-cover object-[50%_35%]"
    />
  </div>
</div>
      </section>

      {/* Vision Mission */}
      <section className="section-pad bg-lavender/60">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="card-luxe p-10">
            <Eye className="w-10 h-10 text-rose" />
            <p className="overline mt-6">Our Vision</p>
            <h2 className="font-display text-3xl text-aubergine mt-3">
              An India where every ambitious woman has a room of her own.
            </h2>
            <p className="text-plumSoft mt-5 leading-relaxed font-light">
              Starting with Hyderabad, then Bengaluru, Chennai, Mumbai and Delhi —
              we are building the highest-trust women-only community in India, one chapter at a time.
            </p>
          </div>
          <div className="card-luxe p-10">
            <Target className="w-10 h-10 text-rose" />
            <p className="overline mt-6">Our Mission</p>
            <h2 className="font-display text-3xl text-aubergine mt-3">
              Empowerment, wellness and CSR — under one roof.
            </h2>
            <p className="text-plumSoft mt-5 leading-relaxed font-light">
              Through hands-on workshops, full-spectrum health & wellness programs,
              and meaningful CSR action for under-served girls and women — we make ambition sustainable.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad container-luxe">
        <p className="overline">What we stand for</p>
        <h2 className="font-display text-4xl md:text-5xl text-aubergine mt-3">Three pillars. One commitment.</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="card-luxe p-8">
              <div className="w-12 h-12 rounded-full bg-lavender text-plum flex items-center justify-center">{v.icon}</div>
              <h3 className="font-display text-2xl text-aubergine mt-6">{v.title}</h3>
              <p className="mt-3 text-plumSoft font-light leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="section-pad bg-aubergine text-ivory">
        <div className="container-luxe">
          <p className="overline !text-champagne">Leadership</p>
          <h2 className="font-display text-4xl md:text-5xl mt-3 max-w-2xl">The women building WOWSHE.</h2>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
            {TEAM.map((t) => (
              <div key={t.name}>
                <div className="aspect-[3/4] overflow-hidden rounded-2xl">
                  <img src={t.img} alt={t.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <h3 className="font-display text-xl mt-5">{t.name}</h3>
                <p className="text-xs uppercase tracking-widest text-champagne mt-1">{t.role}</p>
                <p className="text-sm text-ivory/70 mt-3 font-light">{t.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="section-pad container-luxe">
        <p className="overline">Community Impact · 2026 Plan</p>
        <h2 className="font-display text-4xl md:text-5xl text-aubergine mt-3">Numbers we're building toward.</h2>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: "120+", l: "Founding Members · Hyderabad" },
            { v: "36", l: "Empowerment Workshops" },
            { v: "12", l: "Health & Wellness Programs" },
            { v: "500+", l: "Girls & Women · CSR Reach" },
          ].map((s) => (
            <div key={s.l} className="card-luxe p-8 text-center">
              <p className="font-display text-5xl text-aubergine">{s.v}</p>
              <p className="text-xs uppercase tracking-widest text-plum mt-3">{s.l}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
